<template>
  <div class="checkout-products">
    <checkout-products-group
      v-for="group in groups"
      :key="group.id"
      :group="group"
      :cart-is-loading="cartIsLoading"
      @edit="edit"
    />

    <modal
      :visible="editingOpened"
      @on-close="editingOpened = false"
    >
      <product-editor
        :params="productParams"
        @on-close="editingOpened = false"
      />
    </modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

/* Utils */
import { cartGtmEvents } from '@/gtm/cart-gtm-events';
import { getLineIds } from '@/components/checkout/delivery-selector/utils';
import { dateToVerbal } from '@/utils/date-to-string';

/* Components */
import ProductEditor from '../product/product-editor';
import CheckoutProductsGroup from "./checkout-products-group";

/* Mixins */
import getIntervalDates from '@/mixins/get-interval-dates';

export default {
  name: 'checkout-products',

  components: {
    CheckoutProductsGroup,
    ProductEditor,
  },

  mixins:[getIntervalDates],

  props: {
    cartIsLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      editingOpened: false,
      productParams: {},
    }
  },

  computed: {
    ...mapGetters({
      PICKUP_STORES: 'cart/pickupStores',
      THIRDPARTY_STORES: 'cart/thirdpartyStores',
      SHIPPING_GROUPS: 'cart/shippingGroups',
      DELIVERRY_INFO: 'cart/deliveryInfo',
      DELIVERY_ADDRESS: 'cart/deliveryAddress',
      SELECTED_DATE: 'cart/deliverySelectedDate',
      CART_IS_LOADING: 'cart/isLoading'
    }),

    groups() {
      return this.SHIPPING_GROUPS.map(group => {
        const lineIds = getLineIds(group.products);
        const productsCount = lineIds.length;
        const output = {
          id: group.id,
          products: group.products,
          notification: {},
          actions: [],
          storeId: group.storeId,
        };

        if (group.type === 'pickup') {
          return {
            type: group.type,
            ...output,
            ...this.getPickupThirdPartyOutput(lineIds, group, productsCount),
          }
        }

        if (group.type === 'thirdparty') {
          return {
            type: group.type,
            ...output,
            ...this.getPickupThirdPartyOutput(lineIds, group, productsCount),
            name: this.THIRDPARTY_STORES[group.thirdPartyId]?.name,
            thirdPartyType: this.THIRDPARTY_STORES[group.thirdPartyId]?.thirdPartyStoreType,
          }
        }

        if (group.type === 'delivery') {
          return {
            ...output,
            ...this.getDeliveryOutput(lineIds, group, productsCount),
            type: group.type
          };
        }

        if (group.type === 'unallocated' && this.SHIPPING_GROUPS.length > 1) {
          return {
            ...output,
            ...this.getUnallocatedOutput(lineIds, group, productsCount)
          };
        }

        if (group.type === 'soldout') {
          return {
            ...output,
            ...this.getSoldoutOutput(lineIds)
          };
        }

         return output;
      });
    },
  },

  methods: {
    ...mapActions({
      REMOVE: 'cart/remove',
      SET_SHIPPING_GROUP: 'cart/setShippingGroup',
      RESET_SHIPPING_GROUP: 'cart/resetShippingGroup',
    }),

    allocatePickup(shippingGroup, type = 'pickup') {
      this.$f.$emit('allocate-pickup', { products: shippingGroup.products, shippingGroup, type });
    },

    getProductsVerbal(productsCount) {
      return this.$options.filters.decl(productsCount, 'товар', ['', 'а', 'ов']);
    },

    getVerbalDate(date) {
      return dateToVerbal(new Date(date));
    },

    getPickupThirdPartyOutput(lineIds, group, productsCount) {
      const hasInterval = group?.receivedInterval?.from && group?.receivedInterval?.to;
      const dateText = hasInterval
        ? this.getIntervalDates(group.receivedInterval.from, group.receivedInterval.to)
        : this.getVerbalDate(group.receivedOn);
      const title = `
        Забрать ${productsCount} ${this.getProductsVerbal(productsCount)} самовывозом
        ${dateText}
      `;
      const subtitle = (group.type === 'pickup' ? this.PICKUP_STORES[group.storeId] : this.THIRDPARTY_STORES[group.thirdPartyId])?.address || '';

      return {
        notification: {
          title,
          subtitle,
        },
        flat: true,
        actions: [{
          text: 'Изменить',
          flat: true,
          click: () => this.allocatePickup(group),
        }]
      };
    },

    getDeliveryOutput(lineIds, group, productsCount) {
      const { dateFrom, dateTo, interval } = this.SELECTED_DATE;
      const hasDateInterval = !!dateTo;
      const dateText = hasDateInterval ? this.getIntervalDates(dateFrom, dateTo) : this.getVerbalDate(dateFrom);
      const title = `
        Доставить ${productsCount} ${this.getProductsVerbal(productsCount)} курьером ${dateText}, с ${interval.replace('-', "до")}
      `;
      const subtitle = (this.DELIVERRY_INFO?.address?.streetName && this.DELIVERY_ADDRESS) || 'Адрес не указан';

      return {
        notification: {
          title,
          subtitle
        },
        flat: true,
        actions: [{
          text: 'Изменить',
          flat: true,
          click: () => this.allocatePickup(group, 'courier'),
        }]
      };
    },

    getUnallocatedOutput(lineIds, group, productsCount) {
      const actions = [];

      const isPickUpAvailable = group.shippingChannels?.some(c => c.type === 'pickup' && c.available)
      if (isPickUpAvailable) {
        actions.push({
          text: 'Забрать самовывозом',
          click: () => {
            this.allocatePickup(group);
            cartGtmEvents.shippingByPickup();
          }
        });
      }

      const isDeliveryAvailabe = group.shippingChannels?.some(c => c.type === 'delivery' && c.available);
      if (isDeliveryAvailabe) {
        actions.push({
          text: 'Доставить курьером',
          click: () => {
            this.allocatePickup(group, 'courier');
            cartGtmEvents.shippingByCourier();
          }
        });
      }

      const title = `Не выбран способ получения для ${productsCount} ${this.$options.filters.decl(productsCount, 'товар', ['а', 'ов', 'ов'])}`;
      let subtitle;

      switch(actions.length) { // количество кнопок
        case 1: {
          if (isPickUpAvailable) {
            subtitle = `${productsCount > 1 ? 'Товары доступны' : 'Товар доступен'} только для самовывоза`;
          } else {
            subtitle = `${productsCount > 1 ? 'Товары доступны' : 'Товар доступен'} только для доставки курьером`;
          }
          break;
        }
        default: {
          subtitle = 'Выберите удобный способ получения'
          break;
        }
      }

      return {
        notification: {
          title,
          subtitle,
          color: '#FFB71B',
        },
        actions,
      };
    },

    getSoldoutOutput(lineIds) {
      return {
        notification: {
          title: 'Товар распродан',
          subtitle: 'Для оформления заказа выберите другие параметры товара (размер, цвет) или удалите товар из корзины',
          color: '#FFB71B',
        },
        flat: true,
        actions: [{
          text: 'Удалить',
          flat: true,
          click: () => this.REMOVE({lineIds}),
        }],
      };
    },

    edit({ product, group }) {
      this.productParams = {
        size: product?.models[0]?.skus?.find(sku => sku.available && sku.id === product.skuId)?.size || '',
        quantity: product.quantity,
        id: product.id,
        lineIds: product.lineIds,
        shippingGroupId: group.id,
        type: group.type,
        storeId: group.storeId
      }

      this.editingOpened = true;
    }
  },
};

</script>

<style lang="scss">
.checkout-products {
  & > .modal {
    & > .modal-content {
      max-height: 100%;
      padding: 16px;
      border-radius: 0;
    }
  }
}

@include breakpoint("tablet") {
  .checkout-products {
    & > .modal {
      & > .modal-content {
        width: 768px;
        height: 531px;
        padding: 0;
        border-radius: 0 4px 4px 0;
      }
    }
  }
}

@include breakpoint("desktop") {
  .checkout-products {
    & > .modal {
      & > .modal-content {
        width: 878px;
        height: 613px;
        border-radius: 4px;
      }
    }

    .ui-notification {
      padding: 16px 20px 16px 16px;
    }
  }
}
</style>
